import { Identifiable } from "./Identifiable";

// ─── Typy backendu (zgodne z odpowiedzią getBasket) ────────────────────────────

export type BackendAnswerType = 'Text' | 'TextArea' | 'Radio' | 'CheckBox' | 'Select' | 'AccountIds';

/** Zapis odpowiedzi ankiety — jedna pozycja na instancję (po rozłożeniu qnt). */
export interface SurveyAnswers {
  assortIds: Identifiable;
  exerciseIds: Identifiable;
  questions: AssortQuestion[];
}

/** Body POST zapisu ankiet — facilityIds w body, nie w query. */
export interface SurveyAnswersList {
  surveyList: SurveyAnswers[];
  facilityIds: string;
}

/**
 * Parsuje `assortIds` / `exerciseIds` z koszyka (np. "0,1041" lub samo "55478").
 */
export const parseIdentifiable = (value: string | undefined | null): Identifiable => {
  if (value == null || String(value).trim() === "") {
    return { objectDb: 0, objectId: 0 };
  }
  const parts = String(value)
    .trim()
    .split(",")
    .map((p) => parseInt(p.trim(), 10));
  if (parts.length >= 2 && !parts.slice(0, 2).some((n) => Number.isNaN(n))) {
    return { objectDb: parts[0], objectId: parts[1] };
  }
  if (parts.length >= 1 && !Number.isNaN(parts[0])) {
    return { objectDb: 0, objectId: parts[0] };
  }
  return { objectDb: 0, objectId: 0 };
};

export interface AnswerPOJO {
  objectDb: number;
  objectId: number;
  /** 'A' = normalna odpowiedź, 'W' = wymaga uwagi (ważna biznesowo) */
  status: string;
  displayName?: string;
  statusName?: string | null;
  questionIds: { objectDb: number; objectId: number };
  answer: string;
  description: string;
}

export interface FullQuestionPOJO {
  objectDb: number;
  objectId: number;
  status: string;
  displayName?: string;
  statusName?: string | null;
  surveyIds: { objectDb: number; objectId: number };
  question: string;
  answerType: BackendAnswerType;
  description: string;
  required: boolean;
  /** JSON string z właściwościami lub pusty string */
  properties: string;
  sortingPriority: number;
  options: AnswerPOJO[];
}

export interface FullSurveyPOJO {
  objectDb: number;
  objectId: number;
  status: string;
  displayName?: string;
  statusName?: string | null;
  title: string;
  description: string;
  created: number;
  updated: number;
  sortingPriority: number;
  questions: FullQuestionPOJO[];
}

export interface Survey2AssortSaver {
  assortIds: string;
  sections: FullSurveyPOJO[];
}

export const questionId = (q: { objectDb: number; objectId: number }): string =>
  `${q.objectDb},${q.objectId}`;

/** Sekcje i pytania w kolejności jak w formularzu (sortingPriority). */
export const getSurveySectionsInDisplayOrder = (survey: Survey2AssortSaver): FullSurveyPOJO[] =>
  [...survey.sections]
    .sort((a, b) => a.sortingPriority - b.sortingPriority)
    .map((section) => ({
      ...section,
      questions: [...section.questions].sort((a, b) => a.sortingPriority - b.sortingPriority),
    }));

export const listSurveyQuestionsInDisplayOrder = (
  survey: Survey2AssortSaver
): { section: FullSurveyPOJO; question: FullQuestionPOJO }[] => {
  const rows: { section: FullSurveyPOJO; question: FullQuestionPOJO }[] = [];
  for (const section of getSurveySectionsInDisplayOrder(survey)) {
    for (const question of section.questions) {
      rows.push({ section, question });
    }
  }
  return rows;
};

export interface ValidateRequiredAnswersResult {
  valid: boolean;
  invalidIds: Set<string>;
  firstInvalidId: string | null;
}

/** Wymagane pola bez odpowiedzi — kolejność „od góry” jak w UI. */
export const validateRequiredAnswers = (
  survey: Survey2AssortSaver,
  answers: Record<string, string>
): ValidateRequiredAnswersResult => {
  const invalidIds = new Set<string>();
  let firstInvalidId: string | null = null;
  for (const { question: q } of listSurveyQuestionsInDisplayOrder(survey)) {
    const qid = questionId(q);
    if (q.required && !answers[qid]?.trim()) {
      invalidIds.add(qid);
      if (!firstInvalidId) firstInvalidId = qid;
    }
  }
  return {
    valid: invalidIds.size === 0,
    invalidIds,
    firstInvalidId,
  };
};

// ─── Format zapisu odpowiedzi ───────────────────────────────────────────────────

export interface AssortQuestion {
  surveyIds: string;
  question: string;
  answer: string;
  answerType: BackendAnswerType;
  /** 'A' = normalna, 'W' = wymaga uwagi — backend: Character */
  answerStatus: string;
}

// ─── Typ elementu ankiety w UI ──────────────────────────────────────────────────

export interface BasketSurveyItem {
  assortName: string;
  assortIds: string;
  validFrom: string | null;
  price: number;
  basketLineIndex: number;
  instanceIndex: number;
  survey: Survey2AssortSaver;
}

/** Wartość do wysłania na backend — tekst odpowiedzi z wyjątkiem AccountIds (tam zostaje id). */
export const resolveSurveyAnswerForPayload = (q: FullQuestionPOJO, storedValue: string): string => {
  switch (q.answerType) {
    case "AccountIds":
      return storedValue;
    case "Radio":
    case "Select": {
      const opt = q.options?.find((o) => questionId(o) === storedValue);
      return opt?.answer ?? storedValue;
    }
    case "CheckBox":
      if (q.options?.length) {
        return storedValue;
      }
      return storedValue === "true" ? "Tak" : "Nie";
    default:
      return storedValue;
  }
};
